import React from 'react';
import defaultTo from 'lodash/defaultTo';
import { downloadCreditNotePDF, DownloadQuoteResponseDto, PDFStatus } from '@atlassian/temp-proxies';
import { NotificationTypes, autoRetry, useNotification, errorNotificationBuilder } from '@css-xp/base';
import { NOTIFICATION_DISMISS_TIME, QUOTE_POLLING_INTERVAL, QUOTE_POLLING_MAX_TIMER } from '@css-xp/ppc-constants';

export enum CreditNoteNotificationHeaderMsg {
  PDF_NOT_FOUND = 'Credit note preview',
}

export const useCreditNotePoller = (creditNoteId: string, txaId: string) => {
  const [isDownloadingPdf, setDownloadingPdf] = React.useState<boolean | undefined>(undefined);
  const [creditNoteFileName, setCreditNoteFileName] = React.useState<string | undefined>(undefined);

  const { sendErrorNotification } = useNotification();

  const errorNotificationOptions = errorNotificationBuilder()
    .withId('credit-note-download-error')
    .withDismissAfter(NOTIFICATION_DISMISS_TIME)
    .isVersion(NotificationTypes.FLAG);

  const shouldRetryDownloading = (pdfResponse: DownloadQuoteResponseDto | void): boolean => {
    return pdfResponse?.pdfStatus === PDFStatus.IN_PROGRESS;
  };

  const handleDownloadFailure = () => {
    setDownloadingPdf(false);
    const options = errorNotificationOptions
      .withHeader(CreditNoteNotificationHeaderMsg.PDF_NOT_FOUND)
      .withMessage('Pdf generation has failed. Please retry after sometime')
      .build();
    sendErrorNotification(options);
  };

  const doneDownloadingPdf = (pdfResponse: DownloadQuoteResponseDto) => {
    setDownloadingPdf(false);
    if (pdfResponse.pdfStatus === PDFStatus.NOT_FOUND) {
      const options = errorNotificationOptions
        .withHeader(CreditNoteNotificationHeaderMsg.PDF_NOT_FOUND)
        .withMessage('Pdf generation has not started. Please retry in some time')
        .build();
      sendErrorNotification(options);
    } else {
      if (pdfResponse.pdfUrl) {
        const anchorTag = document.createElement('a');
        anchorTag.href = pdfResponse.pdfUrl;
        anchorTag.download = defaultTo(creditNoteFileName, pdfResponse.pdfUrl);
        anchorTag.click();
      }
    }
  };

  const handleCreditNoteDownload = (fileName: string) => {
    setDownloadingPdf(true);
    setCreditNoteFileName(fileName);
    autoRetry({
      call: async () => downloadCreditNotePDF(creditNoteId, txaId),
      initialTimer: QUOTE_POLLING_INTERVAL, // poll every 5 seconds
      maxTimer: QUOTE_POLLING_MAX_TIMER, // max poll time 2 minutes
      shouldRetry: shouldRetryDownloading,
      onComplete: doneDownloadingPdf,
      numberOfTries: 5,
      backoffFactor: 2,
      onFailure: handleDownloadFailure,
      shouldNotRetryOnFailure: true,
    });
  };

  return { isDownloadingPdf, handleCreditNoteDownload };
};
